"use client";

import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { useV2Translation } from "@/lib/i18n/use-v2-translation";
import Cal from "@calcom/embed-react";
import { getCalLink } from "./config";

// Distinct de CAL_NAMESPACE : le popup peut s'ouvrir sur une page qui a déjà
// un CalInlineEmbed.
const CAL_POPUP_NAMESPACE = "lemanit-booking-popup";

interface CalPopupProps {
  isOpen: boolean;
  onClose: () => void;
  // Type d'événement à réserver, si différent du call de découverte par défaut.
  calLink?: string;
  // Force la langue de l'interface Cal.com (voir CalInlineEmbed) ; par défaut
  // suit `lang` comme sur le site principal.
  locale?: string;
}

export function CalPopup({ isOpen, onClose, calLink, locale }: CalPopupProps) {
  const { lang } = useV2Translation();
  const popupRef = useRef<HTMLDivElement>(null);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={popupRef}
      className="fixed bottom-24 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] max-h-[75vh] bg-paper text-ink rounded-2xl shadow-2xl shadow-navy/30 overflow-hidden animate-in slide-in-from-bottom-5 fade-in duration-300"
      role="dialog"
      aria-modal="true"
      aria-label="Prendre rendez-vous"
    >
      {/* Close button */}
      <div className="absolute top-3 right-3 z-10">
        <button
          type="button"
          onClick={onClose}
          className="w-8 h-8 rounded-full border border-line bg-paper flex items-center justify-center hover:border-gold hover:bg-gold/10 transition-colors"
          aria-label="Fermer"
        >
          <X size={14} strokeWidth={2.5} />
        </button>
      </div>

      {/* Embed Cal.com (et non une iframe brute) : seul le mode embed signale
          les réservations confirmées, suivies par CalBookingTracker. */}
      <div className="relative h-[65vh] max-h-[500px] w-full overflow-y-auto">
        <Cal
          namespace={CAL_POPUP_NAMESPACE}
          calLink={calLink ?? getCalLink(lang)}
          calOrigin="https://cal.leman-it.com"
          embedJsUrl="https://cal.leman-it.com/embed/embed.js"
          style={{ width: "100%", height: "100%" }}
          config={{
            layout: "month_view",
            theme: "light",
            locale: locale ?? lang,
          }}
        />
      </div>
    </div>
  );
}
